import { lazy, Suspense } from 'react';
import { useState, type FormEvent } from 'react';
import { Redirect, Route, Switch, useSearch } from 'wouter';
import { useAuth } from './lib/auth';
import { trackJourney } from './lib/journey';
import { setupUrlAfterAuth } from './lib/navigation';
import Layout from './components/Layout';
import NewPractice from './pages/NewPractice';
import PrivacyPolicy from './pages/PrivacyPolicy';

const PracticeRoom = lazy(() => import('./pages/PracticeRoom'));
const SessionLog = lazy(() => import('./pages/SessionLog'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
type ConsentAction = 'email' | 'google';

function SignIn() {
  const { signInWithEmail, signInWithGoogle, signUpWithEmail, getToken } = useAuth();
  const authSearch = useSearch();
  const authParams = new URLSearchParams(authSearch);
  const [email, setEmail] = useState(authParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [createAccount, setCreateAccount] = useState(true);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [consentAction, setConsentAction] = useState<ConsentAction | null>(null);
  const [dialogAccepted, setDialogAccepted] = useState(false);
  const [error, setError] = useState('');
  const contextIntent = ['file', 'drive'].includes(authParams.get('intent') || '');
  const projectInvitation = window.location.pathname.startsWith('/projects/') && authParams.get('invite') === '1';

  function requestConsent(action: ConsentAction) {
    setError('');
    setDialogAccepted(false);
    setConsentAction(action);
  }

  function closeConsent() {
    setConsentAction(null);
    setDialogAccepted(false);
  }

  function returnToSetup() {
    const destination = setupUrlAfterAuth(new URL(window.location.href));
    if (destination) window.location.replace(destination);
  }

  async function emailAuth() {
    try {
      if (createAccount) {
        await signUpWithEmail(email, password);
        void trackJourney(getToken, 'signup_completed', { properties: { provider: 'email' } });
      } else {
        await signInWithEmail(email, password);
        void trackJourney(getToken, 'signin_completed', { properties: { provider: 'email' } });
      }
      returnToSetup();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message.replace(/^Firebase:\s*/i, '') : 'Authentication failed');
    }
  }

  async function googleAuth() {
    try {
      const outcome = await signInWithGoogle();
      void trackJourney(getToken, outcome === 'signup' ? 'signup_completed' : 'signin_completed', { properties: { provider: 'google' } });
      returnToSetup();
    }
    catch (cause) { setError(cause instanceof Error ? cause.message.replace(/^Firebase:\s*/i, '') : 'Google sign-in failed'); }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (createAccount && !privacyAccepted) {
      requestConsent('email');
      return;
    }
    await emailAuth();
  }

  async function google() {
    setError('');
    if (createAccount && !privacyAccepted) {
      requestConsent('google');
      return;
    }
    await googleAuth();
  }

  async function acceptConsent() {
    if (!dialogAccepted || !consentAction) return;
    const action = consentAction;
    setPrivacyAccepted(true);
    closeConsent();
    if (action === 'google') await googleAuth();
    else await emailAuth();
  }
  return (
    <main className="signin-page">
      <section className="signin-card">
        <img className="brand-mark" src="/brand/maxed-logo.webp" alt="Maxed" />
        <p className="eyebrow">Private practice. Real progress.</p>
        <h1>Walk into your next meeting ready to speak.</h1>
        <p>Practice realistic multilingual meetings by text, voice, or avatar and get a vocabulary report card you can use.</p>
        {contextIntent && <div className="auth-intent"><strong>Sign up for free to add meeting context</strong><span>Your practice description will stay ready after signup.</span></div>}
        {projectInvitation && <div className="auth-intent"><strong>You’ve been invited to a Maxed project</strong><span>Create an account or sign in with the invited email to access its shared assets.</span></div>}
        <button className="button secondary wide google-auth" type="button" onClick={() => void google()}>
          <svg className="google-icon" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285f4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.909c1.702-1.567 2.683-3.874 2.683-6.615Z" />
            <path fill="#34a853" d="M9 18c2.43 0 4.468-.806 5.957-2.18l-2.909-2.258c-.806.54-1.835.859-3.048.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
            <path fill="#fbbc05" d="M3.963 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.281-1.707V4.961H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.039l3.007-2.332Z" />
            <path fill="#ea4335" d="M9 3.579c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.425 0 9 0A9 9 0 0 0 .956 4.961l3.007 2.332C4.672 5.164 6.656 3.579 9 3.579Z" />
          </svg>
          {createAccount ? 'Sign up with Google' : 'Sign in with Google'}
        </button>
        <div className="auth-divider"><span>or</span></div>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email address" required />
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Password" minLength={8} required />
          {error && <p className="auth-error">{error}</p>}
          <button className="button primary wide" type="submit">{createAccount ? 'Create account' : 'Sign in'}</button>
        </form>
        <button className="auth-switch" onClick={() => { setCreateAccount((value) => !value); setPrivacyAccepted(false); setError(''); }}>
          {createAccount ? 'Already have an account? Sign in' : 'New to Maxed? Create an account'}
        </button>
        {createAccount && (
          <label className="auth-consent">
            <input type="checkbox" checked={privacyAccepted} onChange={(event) => setPrivacyAccepted(event.target.checked)} />
            <span>I have read and agree to the <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
          </label>
        )}
        <small>Your setup conversation and practice history remain private. Projects are shared only when an owner invites someone.</small>
      </section>
      {consentAction && (
        <div className="consent-backdrop">
          <section className="consent-dialog" role="dialog" aria-modal="true" aria-labelledby="privacy-consent-title">
            <button className="consent-close" type="button" aria-label="Close privacy consent" autoFocus onClick={closeConsent}>×</button>
            <p className="eyebrow">Before you create an account</p>
            <h2 id="privacy-consent-title">Please review the Privacy Policy</h2>
            <p>Maxed processes account details and meeting context to provide personalized practice. Read and accept the policy to continue signing up.</p>
            <a className="consent-policy-link" href="/privacy-policy" target="_blank" rel="noreferrer">Read the Privacy Policy <span>→</span></a>
            <label className="consent-check">
              <input type="checkbox" checked={dialogAccepted} onChange={(event) => setDialogAccepted(event.target.checked)} />
              <span>I have read and agree to the Privacy Policy.</span>
            </label>
            <div className="consent-actions">
              <button className="button secondary" type="button" onClick={closeConsent}>Cancel</button>
              <button className="button primary" type="button" disabled={!dialogAccepted} onClick={() => void acceptConsent()}>Accept and continue</button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

function NewPracticeRoute() {
  const search = useSearch();
  return <NewPractice key={search} />;
}

function ProjectRoute({ params }: { params: { id: string; tab?: string } }) {
  return <NewPractice key={params.id} projectId={params.id} tab={params.tab} />;
}

export default function App() {
  const { user, loading } = useAuth();
  if (window.location.pathname === '/privacy-policy') return <PrivacyPolicy />;
  if (loading) return <div className="app-loader"><span /></div>;
  if (!user) return <SignIn />;

  return (
    <Layout>
      <Suspense fallback={<div className="loading-panel">Loading...</div>}>
        <Switch>
          <Route path="/" component={NewPracticeRoute} />
          <Route path="/projects/:id/:tab?" component={ProjectRoute} />
          <Route path="/practice" component={PracticeRoom} />
          <Route path="/sessions/:id" component={SessionLog} />
          <Route path="/admin">{user.isAdmin ? <AdminDashboard /> : <Redirect to="/" />}</Route>
          <Route><Redirect to="/" /></Route>
        </Switch>
      </Suspense>
    </Layout>
  );
}
